import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveState } from '../../stores/store';
import { loginUser as apiLoginUser, verifyOtp } from '../../api/user';
import {
    LockOutlined,
    UserOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    StarOutlined,
    ShoppingOutlined,
    SnippetsOutlined,
    ShopOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import {
    Button,
    Checkbox,
    Form,
    Input,
    Flex,
    Divider,
    Space,
    Alert,
    Typography,
    Modal,
    message
} from 'antd';
import { useSettings } from '../../contexts/settingsContext';
import { fetchAndSetCompetition } from '../../helpers/fetchAndSetCompetition';
import { fetchAndSetSettings } from '../../helpers/fetchAndSetSettings';
import { requestPasswordReset } from '../../api/user';
const { Title, Text, Link } = Typography;

const Login = () => {
    const navigate = useNavigate();
    const { state: settings, dispatch } = useSettings();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loggingIn, setLoggingIn] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [otp, setOtp] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);
    const [forgotPasswordModalVisible, setForgotPasswordModalVisible] = useState(false);  
    const [resetEmail, setResetEmail] = useState(''); 
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);  

    const loginUser = async () => {
        try {
            setLoggingIn(true);
            setError(null);
            const data = await apiLoginUser(email, password);

            saveState(data);
            await fetchAndSetCompetition(dispatch);
            const selectedCompetition = JSON.parse(localStorage.getItem('selectedCompetition'));
            if (selectedCompetition) {
                await fetchAndSetSettings(dispatch, selectedCompetition._id);
            }

            if (data.user.role === 'admin') {
                console.log('admin');
                navigate("/contestants", { replace: true });
            } else if (data.user.role === 'guardian') {
                console.log('guardian');
                navigate("/contestants", { replace: true });
            } else if (data.user.role === 'contestant') {
                console.log('contestant');
                navigate("/applications", { replace: true });
            } else if (data.user.role === 'judge') {
                console.log('judge');
                navigate("/judging", { replace: true });
            }
        } catch (err) {
            setLoggingIn(false);
            if (err.message.includes('Please verify your email before logging in')) {  
                setIsModalVisible(true);
            }  else {
                setError('Invalid credentials');
            }
        }
    };

    useEffect(() => {  
        const fetchSettingsOnMount = async () => {  
            await fetchAndSetCompetition(dispatch);  
            const selectedCompetition = JSON.parse(localStorage.getItem('selectedCompetition'));  
            if (selectedCompetition) {  
                await fetchAndSetSettings(dispatch, selectedCompetition._id);  
            }  
        };  
        fetchSettingsOnMount();  
    }, [dispatch]);  

    const handleModalOk = async () => {
        try {
            setVerifying(true);
            setVerifyError(null);
            const verifyMessage = await verifyOtp(email, otp);
            if (verifyMessage.message === 'Email verified successfully') {
                setLoggingIn(true);
                const data = await apiLoginUser(email, password);
                saveState(data);
                if (data.user.role === 'admin') {
                    navigate("/statistics", { replace: true });
                } else if (data.user.role === 'guardian') {
                    navigate("/contestants", { replace: true });
                } else if (data.user.role === 'contestant') {
                    navigate("/applications", { replace: true });
                }
            }
        } catch (e) {
            console.error('Invalid OTP. Please try again.');
            setVerifyError('Invalid OTP. Please try again.');
            setVerifying(false);
            setError('Invalid OTP');
        }
        
    }

    const handleOtpChange = (text) => {
        const ask = text
        setOtp(ask);
    };

    const sharedProps = {
        handleOtpChange,
    };

    const handleForgotPassword = async () => {  
        setForgotPasswordLoading(true);
        try {  
            await requestPasswordReset(resetEmail, window.location.origin);  
            message.success('Password reset email sent!');  
            setForgotPasswordModalVisible(false);  
            setResetEmail('');
        } catch (error) {  
            message.error('Error sending password reset email');  
        } finally {
            setForgotPasswordLoading(false);
        }
    };  

    return (
        <div className="login_page">
            <div className='login_container'>
                <div className='login_container_col' style={{ background: 'url(/images/login/background.webp)' }}>
                    {settings.imagePath && <img src={settings.imagePathWhite} alt="logo" />}
                </div>
                <div className='login_container_col'>
                    {settings.imagePath && <img className='login_logo_mobile' src={settings.imagePath} alt="logo" />}
                    <Title level={3}>Login</Title>
                    <Form
                        className='login_form'
                        name="login"
                        initialValues={{ remember: true }}
                        style={{ maxWidth: 360 }}
                        onFinish={loginUser}
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Please input your Username!' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                size="large"
                                placeholder="Username"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value) }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Please input your Password!' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                size="large"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value) }}
                                visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>
                        <Form.Item>  
                        <Flex justify="space-between" align="center">  
                            <Form.Item name="remember" valuePropName="checked" noStyle>  
                                <Checkbox>Remember me</Checkbox>  
                            </Form.Item>  
                            <a href="#" onClick={() => setForgotPasswordModalVisible(true)}>Forgot password</a>  
                        </Flex>  
                    </Form.Item>  
                    <Modal  
                        title="Forgot Password"  
                        visible={forgotPasswordModalVisible}  
                        onOk={handleForgotPassword}  
                        onCancel={() => setForgotPasswordModalVisible(false)}  
                        okText="Submit"  
                        confirmLoading={forgotPasswordLoading}
                    >  
                        <Space direction='vertical' size="middle" style={{ width: '100%' }}>  
                            <Text>Please enter your email address to reset your password.</Text>  
                            <Input  
                                placeholder="Enter your email"  
                                value={resetEmail}  
                                size='large'  
                                onChange={(e) => setResetEmail(e.target.value)}  
                                style={{ width: '100%' }}  
                            />  
                        </Space>  
                    </Modal>  
                        <Form.Item>
                            <Button loading={loggingIn} block type="primary" shape='round' size="large" htmlType="submit">
                                Log in
                            </Button>
                            {error && <Alert message={error} type="error" showIcon style={{ marginTop: '15px' }} />}
                        </Form.Item>
                    </Form>
                    <Space direction='vertical' size="middle" style={{ width: '100%', maxWidth: '300px' }}>
                    {settings.selectedRound === 1 && (  
                        <>  
                        <Button  
                        block  
                        icon={<StarOutlined />}  
                        onClick={() => {  
                          Modal.confirm({  
                            title: 'Confirm Solo Registration',  
                            content: "Are you sure you want to register as a solo contestant? You will not be able to create a group later if you select this option.",  
                            okText: 'Yes, Continue',  
                            cancelText: 'Cancel',  
                            onOk: () => navigate('/apply'),  
                          });  
                        }}  
                      >  
                        Register as Solo Contestant  
                      </Button>  
                          <Button  
                            block  
                            icon={<SnippetsOutlined />}  
                            onClick={() => navigate('/register')}  
                          >  
                            Register as Guardian / Coach for Group Entries
                          </Button>  
                        </>  
                      )}
                    </Space>
                </div>
            </div>
            <Modal
                title="Email Verification"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setIsModalVisible(false)
                    setOtp('');   
                    setVerifyError(null); 
                }}
                okText="Verify and Proceed"
                confirmLoading={verifying}
                okButtonProps={{ icon: <CheckCircleOutlined />, disabled: otp.length !== 4 }}
            >
                <Space direction='vertical' size="middle" style={{ width: '100%' }}>
                    <Text>Please enter the OTP that was sent to your email to verify your account.</Text>
                    <Input.OTP
                        placeholder="Enter OTP"
                        value={otp}
                        size='large'
                        onChange={handleOtpChange}
                        length={4}   
                        {...sharedProps}
                        style={{ width: '100%', letterSpacing: '0.5em' }}    
                    />
                    {verifyError && <Alert showIcon type='error' message={verifyError} />}
                </Space>
            </Modal>
        </div>
    );
};

export default Login;  
